import { useState, useEffect } from "react";
import { Box, Grid, Typography, useTheme } from "@mui/material";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, BarChart, Bar } from "recharts";
import { useThemeContext } from "../context/ThemeContext";
import { fetchDashboardAnalytics, fetchSalesTrends, fetchTopProducts } from "../api/analyticsApi";
import { eachDayOfInterval, format, isAfter } from "date-fns";

const normalizeSalesTrends = (rawData: any[]) => {
  if (!rawData.length) return [];
  const now = new Date();

  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startDate = new Date(rawData[0].date);
  const dataEndDate = new Date(rawData[rawData.length - 1].date);

  
  // Use today as end date if data ends before today
  const endDate = isAfter(today, dataEndDate) ? today : dataEndDate;

  const allDates = eachDayOfInterval({ start: startDate, end: endDate });
  const map = new Map(rawData.map(d => [format(new Date(d.date), 'yyyy-MM-dd'), d.sales]));

  return allDates.map(date => ({
    date: date.toISOString(),
    sales: map.get(format(date, 'yyyy-MM-dd')) ?? 0
  }));
};


const StatsCard = ({ title, value, change, today, yesterday }: any) => {
  const { isDarkMode } = useThemeContext();
  const theme = useTheme();

  return (
    <Box sx={{ 
      backgroundColor: isDarkMode ? theme.palette.background.paper : "#fff", 
      borderRadius: 2, 
      p: 3, 
      boxShadow: theme.shadows[1] 
    }}>
      <Typography variant="subtitle2" color="text.secondary">{title}</Typography>
      <Box mt={1} display="flex" alignItems="baseline">
        <Typography variant="h4">{value}</Typography>
        <Typography variant="body2" ml={1.5} color={change.startsWith("+") ? "success.main" : "error.main"}>
          {change}
        </Typography>
      </Box>
      <Box mt={2} display="flex" justifyContent="space-between">
        <Typography variant="caption">Today: {today}</Typography>
        <Typography variant="caption">Yesterday: {yesterday}</Typography>
      </Box>
    </Box>
  );
};

const DashboardOverview = () => {
  const { isDarkMode } = useThemeContext();
  const theme = useTheme();
  const [dashboardData, setDashboardData] = useState<any>(null);
  const [salesTrends, setSalesTrends] = useState<any[]>([]);
  const [topProducts, setTopProducts] = useState<any[]>([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [analytics, trends, products] = await Promise.all([
          fetchDashboardAnalytics(),
          fetchSalesTrends(),
          fetchTopProducts()
        ]);
  
        setDashboardData(analytics);
        setSalesTrends(normalizeSalesTrends(trends)); // 🧙 magic here
        setTopProducts(products);
      } catch (error) {
        console.error("Error fetching analytics data:", error);
      }
    };
  
    fetchData();
  }, []);
  

  if (!dashboardData) return <Typography>Loading...</Typography>;

  return (
    <Box sx={{ flexGrow: 1 }}>
      <Typography variant="h4" mb={4}>Sales Dashboard</Typography>

      {/* Key Stats Grid */}
      <Grid container spacing={3} mb={4}>
        <Grid size={{xs: 12, md: 3}}>
          <StatsCard 
            title="Total Sales" 
            value={`$${dashboardData.totalSales || 0}`} 
            change="+51%" 
            today={`$${dashboardData.todaySales || 0}`} 
            yesterday={`$${dashboardData.yesterdaySales || 0}`} 
          />
        </Grid>
        <Grid size={{xs: 12, md: 3}}>
          <StatsCard 
            title="Active Customers" 
            value={dashboardData.activeCustomers || 0} 
            change="+12%" 
            today={dashboardData.activeCustomersToday || 0} 
            yesterday={dashboardData.activeCustomersYesterday || 0} 
          />
        </Grid>
        <Grid size={{xs: 12, md: 3}}>
          <StatsCard 
            title="Conversion Rate" 
            value={`${(Number(dashboardData.conversionRate) || 0).toFixed(2)}%`} 
            change="+2%" 
            today={`${(Number(dashboardData.conversionRateToday) || 0).toFixed(2)}%`} 
            yesterday={`${(Number(dashboardData.conversionRateYesterday) || 0).toFixed(2)}%`} 
          />
        </Grid>
        <Grid size={{xs: 12, md: 3}}>
          <StatsCard 
            title="Avg. Order Value" 
            value={`$${(Number(dashboardData.avgOrderValue) || 0).toFixed(2)}`} 
            change="+8%" 
            today={`$${(Number(dashboardData.avgOrderValueToday) || 0).toFixed(2)}`} 
            yesterday={`$${(Number(dashboardData.avgOrderValueYesterday) || 0).toFixed(2)}`} 
          />
        </Grid>
      </Grid>

      {/* Charts Section */}
      <Grid container spacing={3}>
        <Grid size={{xs:12, md: 8}}>
          <Box sx={{ 
            bgcolor: "background.paper", 
            p: 3, 
            borderRadius: 2, 
            height: 400 
          }}>
            <Typography variant="h6" mb={2}>Sales Trends</Typography>
            <ResponsiveContainer width="100%" height="95%">
              <LineChart data={salesTrends}>
                <XAxis 
                  dataKey="date" 
                  stroke={theme.palette.text.secondary}
                  tickFormatter={(date) => new Date(date).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric'
                  })}
                  tick={{ fill: theme.palette.text.secondary }}
                />
                <YAxis 
                  stroke={theme.palette.text.secondary}
                  domain={[0, (dataMax: number) => Math.ceil(dataMax * 1.1)]}
                  tick={{ fill: theme.palette.text.secondary }}
                />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: isDarkMode ? "#1e1e1e" : "#fff", 
                    borderColor: theme.palette.divider 
                  }}
                  labelFormatter={(label) => new Date(label).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric'
                  })}
                />
                <Line 
                  type="monotone" 
                  dataKey="sales" 
                  stroke={theme.palette.primary.main} 
                  strokeWidth={2}
                  dot={{ fill: theme.palette.primary.main }}
                />
              </LineChart>
            </ResponsiveContainer>
          </Box>
        </Grid>

        <Grid size={{xs: 12, md: 4}}>
          <Box sx={{ 
            bgcolor: "background.paper", 
            p: 3, 
            borderRadius: 2, 
            height: 400 
          }}>
            <Typography variant="h6" mb={2}>Top Selling Products</Typography>
            <ResponsiveContainer width="100%" height="95%">
              <BarChart data={topProducts}>
                <XAxis 
                  dataKey="product_name" 
                  stroke={theme.palette.text.secondary}
                  tick={{ fill: theme.palette.text.secondary }}
                />
                <YAxis 
                  stroke={theme.palette.text.secondary}
                  tick={{ fill: theme.palette.text.secondary }}
                />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: isDarkMode ? "#1e1e1e" : "#fff", 
                    borderColor: theme.palette.divider 
                  }}
                />
                <Bar 
                  dataKey="total_sold" 
                  fill={theme.palette.primary.main} 
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default DashboardOverview;